// =====================================================================
// Custom popup component
// =====================================================================
class CustomPopup extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("[data-popup-close]").forEach((closeTrigger) => {
      closeTrigger.addEventListener("click", this.hide.bind(this, false));
    });
    this.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") this.hide();
    });

    // this.addEventListener('click', (event) => {
    //     if (event.target === this) this.hide();
    // });
  }

  show() {
    document.body.classList.add("overflow-hidden");
    this.classList.add("visible");
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    this.classList.remove("visible");
  }
}
customElements.define("custom-popup", CustomPopup);

// =====================================================================
// Custom popup opener — triggers a popup by click
// =====================================================================
class CustomPopupOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");
    if (!button) return;

    button.addEventListener("click", () => {
      const popup = document.querySelector(this.getAttribute("data-popup"));
      if (popup) popup.show();
    });
  }
}
customElements.define("custom-popup-opener", CustomPopupOpener);

// =====================================================================
// Tooltip component
// =====================================================================
class ToolTip extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.inner = this.querySelector("[data-tool-tip-inner]");
    this.closeButton = this.querySelector("[data-tool-tip-close]");
    this.toolTipContent = this.querySelector("[data-tool-tip-content]");
    this.toolTipTitle = this.querySelector("[data-tool-tip-title]");

    this.triggers = document.querySelectorAll("[data-tool-tip-trigger]");

    document.addEventListener("tooltip:open", (e) => {
      this._open(e.detail.context, e.detail.content);
    });
  }

  _open(context, insertedHtml) {
    this.toolTipContent.innerHTML = insertedHtml;

    // Only show a title for product availability tooltips
    if (context != "store-availability") {
      this.toolTipTitle.remove();
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        this._close();
      });
    }

    document.documentElement.addEventListener("click", () => {
      this._close();
    });

    document.documentElement.addEventListener("keydown", (event) => {
      if (event.code === "Escape") this._close();
    });

    this.el.dataset.toolTipOpen = true;
    this.el.dataset.toolTip = context;
  }

  _close() {
    this.toolTipContent.innerHTML = "";
    this.el.dataset.toolTipOpen = "false";
    this.el.dataset.toolTip = "";

    this.closeButton.removeEventListener("click");
    document.documentElement.removeEventListener("click");
    document.documentElement.removeEventListener("keydown");
  }
}

customElements.define("tool-tip", ToolTip);

// =====================================================================
// Tooltip trigger — opens a tooltip via custom event
// =====================================================================
class ToolTipTrigger extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.toolTipContent = this.querySelector("[data-tool-tip-content]");
    this.init();
  }

  init() {
    const toolTipOpen = new CustomEvent("tooltip:open", {
      detail: {
        context: this.dataset.toolTip,
        content: this.toolTipContent.innerHTML,
      },
      bubbles: true,
    });

    this.el.addEventListener("click", (e) => {
      e.stopPropagation();
      this.dispatchEvent(toolTipOpen);
    });
  }
}
customElements.define("tool-tip-trigger", ToolTipTrigger);

// =====================================================================
// A/B test: variant 1 — sticky ATC button when scrolled out of view
// (only fires when the .test-1-variant-1 class is present on the page)
// =====================================================================
if (document.querySelector('.test-1-variant-1') && window.location.pathname.includes('/products/')) {
  const handleATCSticky = (e) => {
    const atcButton = document.querySelector('.test-1-variant-1 form[id^=product-form-] .add-to-cart-container button[type=submit]');
    const atcButtonContainer = document.querySelector('.test-1-variant-1 form[id^=product-form-] .add-to-cart-container');
    if (atcButtonContainer.getBoundingClientRect().bottom < 0) {
      // ATC scrolled out of view: pin it to the bottom of the viewport
      atcButtonContainer.classList.add('sticky');
    } else {
      atcButtonContainer.classList.remove('sticky');
    }
  };

  document.addEventListener('scroll', handleATCSticky);
}

// =====================================================================
// A/B test: variant 5 — homepage horizontal collection scroll
// Adds a "reachedEnd" class when user has scrolled to the end of the
// horizontal grid (used to fade out the right-side scroll hint icon)
// =====================================================================
function initHorizontalScrollCheck() {
  if (!document.querySelector('.test-5-variant-1')) return;

  const container = document.querySelector('.homepage-visual-category');
  const grid = container?.querySelector('.grid');

  if (!container || !grid) return;

  grid.addEventListener('scroll', () => {
    const isAtEnd = Math.ceil(grid.scrollLeft) + grid.clientWidth >= grid.scrollWidth - 50;

    if (isAtEnd) {
      container.classList.add('reachedEnd');
    } else {
      container.classList.remove('reachedEnd');
    }
  });
}
initHorizontalScrollCheck();

// =====================================================================
// A/B test: variant 2 — PDP layout overhaul
// Re-arranges the product page layout (vertical thumbnails, merged
// price+rating, hero video relocation, etc.) when the .test-2-variant
// class is present.
// =====================================================================
class PDPTestVariant {
  constructor() {
    this.isVariant = document.querySelector('.test-2-variant');
    if (!this.isVariant) return;

    this.init();
  }

  init() {
    this.mergePriceAndReviews();
    this.injectSentimentText();
    this.moveSizeGuideLink();
    this.moveVendorText();
    this.moveBenefits();
    this.hideEmptyAppDivs();
    this.moveHeroVideo();
    this.injectVerticalThumbnails();
  }

  // Wraps the price block + ratings widget in a single flex container
  mergePriceAndReviews() {
    const priceBlock = document.querySelector('.product-price-block');
    const ratingsWidget = document.querySelector('.klaviyo-star-rating-widget')?.closest('.mt-8');

    if (priceBlock && ratingsWidget) {
      const combinedContainer = document.createElement('div');
      combinedContainer.classList.add('combined-price-rating-container');

      priceBlock.parentNode.insertBefore(combinedContainer, priceBlock);
      combinedContainer.appendChild(ratingsWidget);
      combinedContainer.appendChild(priceBlock);
    }
  }

  // Pulls the hero video's text content into a sentiment message under the title
  injectSentimentText() {
    const titleBlock = document.querySelector('.product-title-block');
    // Only inject if there's actual sentiment text in the hero video
    const originalSentimentText = document.querySelector('.pdp-hero-video');

    if (titleBlock && originalSentimentText && originalSentimentText.innerText != '') {
      const sentimentText = document.createElement('div');
      sentimentText.classList.add('product-sentiment-message');
      sentimentText.innerText = originalSentimentText.innerText.replace(/\s+/g, ' ').trim();
      titleBlock.after(sentimentText);
    }
  }

  // Moves the size guide block inside the variant picker
  moveSizeGuideLink() {
    const sizeGuide = document.querySelector('.size-guide');
    const variantPicker = document.querySelector('.product-variant-picker-block');

    if (sizeGuide && variantPicker) {
      variantPicker.appendChild(sizeGuide);
    }
  }

  // Moves the vendor text below the price/rating combined container
  moveVendorText() {
    const vendorBlock = document.querySelector('.vendorText');
    const combinedPrice = document.querySelector('.combined-price-rating-container');

    if (vendorBlock && combinedPrice) {
      combinedPrice.after(vendorBlock);
    }
  }

  // Moves the benefits icon list to appear right after the ATC section
  moveBenefits() {
    const benefits = document.querySelector('.benefits');
    const atcSection = document.querySelector('[x-data="ProductForm"]');

    if (benefits && atcSection) {
      atcSection.after(benefits);
    }
  }

  // Hides empty app blocks (Simple Bundles, Globo Product Groups) so
  // they don't leave empty .mt-8 wrappers on the page
  hideEmptyAppDivs() {
    const appDivsToCheck = document.querySelectorAll('#simple-bundles-io-options, .globo-product-groups-detail');
    appDivsToCheck.forEach((div) => {
      if (div.children.length === 0) {
        div.closest('.mt-8').style.display = 'none';
      }
    });
  }

  // Maps each product template to a specific hero video URL and
  // moves the hero video block to the new location
  moveHeroVideo() {
    const videosAndTemplateMap = {
      "douspartgold": "https://cdn.shopify.com/videos/c/o/v/ed1059af3a584f1db6b5615f2f03eabd.mp4",
      "douspartsilver": "https://cdn.shopify.com/videos/c/o/v/5fee6f92ae3b4652acae2433401d2593.mp4",
      "mementogold": "https://cdn.shopify.com/videos/c/o/v/8611bacdc66147d0b41085b3fde81860.mp4",
      "memento": "https://cdn.shopify.com/videos/c/o/v/371b55a5bb804a1e8f489cfc838f6fa9.mp4",
      "sacredheartgold": "https://cdn.shopify.com/videos/c/o/v/d9ea3469c4fe4d0abcedfcf146790320.mp4",
      "sacredheart": "https://cdn.shopify.com/videos/c/o/v/6ba78aa5550044beb2fe10192abfb8b7.mp4",
      "tilldeathdiamond": "https://cdn.shopify.com/videos/c/o/v/230fa706f02a4bd8a401f1ff3552c21a.mp4",
      "tilldeathsolidgold": "https://cdn.shopify.com/videos/c/o/v/8a5b7c2310d7402f99c0da7c4a8ec0ec.mp4",
      "tilldeathgold": "https://cdn.shopify.com/videos/c/o/v/5e9b9f1c7a714b1bb9d1e13a0fef7a55.mp4",
      "breakable-goldsilver": "https://cdn.shopify.com/videos/c/o/v/62ea000ac8d043da8c6f54c6aea7b130.mp4",
      "breakable-heart": "https://cdn.shopify.com/videos/c/o/v/d71832b546d94fb8bbf36a4d059d2e4a.mp4",
      "tilldeath": "https://cdn.shopify.com/videos/c/o/v/5e9b9f1c7a714b1bb9d1e13a0fef7a55.mp4"
    };

    const template = document.querySelector('body').getAttribute('data-template');
    const newVideoURL = videosAndTemplateMap[template];

    const heroVideo = document.querySelector('.pdp-hero-video');
    const newHeroLocation = document.querySelector('.pdp-hero-new-place');
    if (heroVideo && newHeroLocation) {
      if (newVideoURL) heroVideo.querySelector('video source').src = newVideoURL;
      newHeroLocation.after(heroVideo);
    }
  }

  // Builds a vertical thumbnail rail beside the main media slideshow
  injectVerticalThumbnails() {
    const mediaContainer = document.querySelector('.product-media-container');

    // Collect all slides — each <li> contains an <img> we can clone
    let slides = mediaContainer
      ? [...mediaContainer.querySelectorAll('.splide__slide')]
      : [];

    if (!mediaContainer || slides.length < 2) return;

    // Build the thumb rail
    const rail = document.createElement('div');
    rail.classList.add('pdp-thumb-rail');

    slides.forEach((slide, i) => {
      if (i === 1 && document.querySelector('.test-2-variant-2')) {
        const heroVideo = document.querySelector('.pdp-hero-video video');
        if (heroVideo) {
          const videoThumb = document.createElement('button');
          videoThumb.classList.add('pdp-thumb', 'video-thumb');
          videoThumb.setAttribute('aria-label', 'View video');

          const vImg = document.createElement('img');
          // Use the video's poster if available, otherwise a fallback
          vImg.src = heroVideo.poster || '';
          vImg.width = 80;
          vImg.height = 80;
          vImg.style.objectFit = 'cover';

          videoThumb.appendChild(vImg);
          rail.appendChild(videoThumb);

          videoThumb.addEventListener('click', () => {
            const movedVideo = document.querySelector('.moved-video');
            movedVideo?.closest('.splide__slide')?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest'
            });
          });
        }
      }

      const sourceImg = slide.querySelector('img');
      if (!sourceImg) return;

      const thumb = document.createElement('button');
      thumb.classList.add('pdp-thumb');
      if (i == 0) thumb.classList.add('pdp-thumb--active');
      thumb.setAttribute('aria-label', `View image ${i + 1}`);
      thumb.dataset.thumbIndex = i;

      const img = document.createElement('img');
      img.src = sourceImg.src;
      img.alt = sourceImg.alt || '';
      img.loading = 'lazy';
      img.width = 80;
      img.height = 80;

      // Use a higher-resolution src from srcset when available
      if (sourceImg.srcset) {
        // Pick the smallest entry from srcset for the thumb
        const firstSrcset = sourceImg.srcset.split(',')[0].trim().split(' ')[0];
        if (firstSrcset) img.src = firstSrcset;
      }

      thumb.appendChild(img);
      rail.appendChild(thumb);

      // Click on a thumb scrolls the corresponding main image into view
      thumb.addEventListener('click', () => {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    });

    // Insert the rail BEFORE the splide so it sits to the left
    const splide = mediaContainer.querySelector('.splide--product');
    if (splide) {
      // Wrap splide + rail together so they can flex side by side
      const wrapper = document.createElement('div');
      wrapper.classList.add('pdp-media-with-thumbs');

      if (document.querySelector('.test-2-variant-2')) {
        const heroVideo = document.querySelector('.pdp-hero-video video');
        if (heroVideo) {
          const cloneVideo = heroVideo.cloneNode(true);
          cloneVideo.classList.add('moved-video');
          const clonedHeroVideoWrapper = document.createElement('li');
          clonedHeroVideoWrapper.classList.add('splide__slide', 'bg-scheme-background', 'max-lg:[.splide:not(.is-active)_&]:w-full');
          clonedHeroVideoWrapper.appendChild(cloneVideo);
          splide.querySelector('.splide__list').insertBefore(clonedHeroVideoWrapper, splide.querySelectorAll('.splide__list li.splide__slide')[1]);
        }
      }

      splide.parentNode.insertBefore(wrapper, splide);
      wrapper.appendChild(splide);
      wrapper.appendChild(rail);
    }

    slides = mediaContainer
      ? [...mediaContainer.querySelectorAll('.splide__slide')]
      : [];
    // Set up the scroll observer to track which thumb is active
    this._setupThumbObserver(slides, rail);
  }

  // Watches scroll position and marks the closest slide's thumb as active
  _setupThumbObserver(slides, rail) {
    const thumbButtons = [...rail.querySelectorAll('.pdp-thumb')];

    let activeIndex = 0;
    let rafId = null;

    const setActive = (index) => {
      if (index === activeIndex) return;
      activeIndex = index;
      thumbButtons.forEach((btn, i) => {
        btn.classList.toggle('pdp-thumb--active', i === index);
      });
    };

    // Pick whichever slide's top edge is closest to the viewport midpoint.
    // One deterministic winner, zero flicker.
    const pick = () => {
      const mid = window.innerHeight / 2;
      let bestIdx = 0;
      let bestDist = Infinity;

      slides.forEach((slide, i) => {
        const { top } = slide.getBoundingClientRect();
        // Distance from the slide's top edge to the viewport midpoint
        const dist = Math.abs(top - mid);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });

      setActive(bestIdx);
      rafId = null;
    };

    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(pick);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Run once on init so the first thumb starts as active
    pick();
  }
}

new PDPTestVariant();

// =====================================================================
// Homepage slideshow — resume autoplay after a manual interaction
// (clicking a pagination dot or arrow). Without this, Splide pauses
// autoplay as soon as the user interacts.
// =====================================================================
(function () {
  function attachAutoplayResume(splideEl) {
    if (!splideEl) return;
    if (splideEl.dataset.autoplayResumeAttached === 'true') return;
    splideEl.dataset.autoplayResumeAttached = 'true';

    splideEl.addEventListener('click', function (e) {
      const trigger = e.target.closest('.splide__pagination__page, .splide__arrow');
      if (!trigger) return;

      setTimeout(function () {
        try {
          const alpineData = window.Alpine && window.Alpine.$data(splideEl);
          if (
            alpineData &&
            alpineData.autoplay &&
            alpineData.splide &&
            alpineData.splide.Components &&
            alpineData.splide.Components.Autoplay
          ) {
            alpineData.splide.Components.Autoplay.play();
          }
        } catch (err) {
          // silently ignore
        }
      }, 150);
    });
  }

  function setupAll() {
    document.querySelectorAll('.splide').forEach(function (splideEl) {
      const xData = splideEl.getAttribute('x-data') || '';
      if (!xData.includes('Slideshow(')) return;
      attachAutoplayResume(splideEl);
    });
  }

  function init() {
    setupAll();
    setTimeout(setupAll, 1000);
    setTimeout(setupAll, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', setupAll);
})();

// =====================================================================
// Homepage slideshow — enable swipe/drag on the FIRST slideshow even
// when autoplay is on. The theme disables drag by default whenever
// autoplay is enabled, so we re-enable it for the first one only.
// =====================================================================
(function () {
  function enableDragOnFirstSlideshow() {
    const splideEls = document.querySelectorAll('.splide');
    let first = null;
    for (let i = 0; i < splideEls.length; i++) {
      const xData = splideEls[i].getAttribute('x-data') || '';
      if (xData.includes('Slideshow(')) { first = splideEls[i]; break; }
    }
    if (!first) return false;
    if (first.dataset.dragEnabledByCustom === 'true') return true;

    try {
      const data = window.Alpine && window.Alpine.$data(first);
      if (data && data.splide && data.splide.Components && data.splide.Components.Drag) {
        data.splide.Components.Drag.disable(false);
        first.dataset.dragEnabledByCustom = 'true';

        // Bonus: after a swipe, resume autoplay (same as we do for clicks)
        if (data.autoplay && data.splide.Components.Autoplay) {
          data.splide.on('dragged', function () {
            data.splide.Components.Autoplay.play();
          });
        }
        return true;
      }
    } catch (err) { /* silent */ }
    return false;
  }

  function init() {
    if (enableDragOnFirstSlideshow()) return;
    setTimeout(enableDragOnFirstSlideshow, 500);
    setTimeout(enableDragOnFirstSlideshow, 1500);
    setTimeout(enableDragOnFirstSlideshow, 3000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();

// =====================================================================
// PDP — Single product variant picker setup
// Moves the size guide block next to the size legend so it appears
// inline ("Size:  📏 Size Guide" instead of as a separate block below).
// =====================================================================
(function () {
  function setupVariantPicker() {
    const variantPicker = document.querySelector('.product-variant-picker-block');
    if (!variantPicker) return;
    if (variantPicker.dataset.compactSetup === 'true') return;
    variantPicker.dataset.compactSetup = 'true';

    const legend = variantPicker.querySelector('legend');
    if (!legend) return;

    const sizeGuide = document.querySelector('.size-guide');
    if (sizeGuide && !variantPicker.contains(sizeGuide)) {
      let originalWrapper = sizeGuide.closest('.mt-8');
      if (originalWrapper && variantPicker.contains(originalWrapper)) originalWrapper = null;

      const headingRow = document.createElement('div');
      headingRow.className = 'pdp-size-heading-row';
      legend.parentNode.insertBefore(headingRow, legend);
      headingRow.appendChild(legend);
      sizeGuide.classList.add('pdp-size-guide-inline');
      headingRow.appendChild(sizeGuide);

      if (originalWrapper && originalWrapper.textContent.trim() === '') {
        originalWrapper.style.display = 'none';
      }
    }
  }

  function init() {
    setupVariantPicker();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 150));
  } else {
    setTimeout(init, 150);
  }
})();

// =====================================================================
// Infinite scroll for collection pages
// + Scroll position / anchor memory (CLP/PLP -> PDP -> back)
//
// Root cause of the scroll-reset bug: this block sets
// `history.scrollRestoration = 'manual'` (required for infinite scroll
// to behave predictably), but never implemented the manual restore
// that setting requires — so on back navigation the browser did
// nothing and landed at the top. Fixed below.
//
// v2: only restores scroll on an actual back/forward navigation
// (checked via the Navigation Timing API), never on a plain refresh;
// catch-up page fetches run in parallel instead of one-by-one for a
// faster restore; shows "Restoring your place..." while it catches up.
// =====================================================================
(function () {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  // --------------------------- SCROLL MEMORY ---------------------------
  function lcScrollMemoryKey() {
    return 'lc-collection-scroll:' + location.pathname + location.search;
  }

  function lcSaveScrollMemory(grid) {
    if (!grid) grid = findGrid(document);
    if (!grid) return;
    try {
      sessionStorage.setItem(
        lcScrollMemoryKey(),
        JSON.stringify({
          scrollY: window.scrollY,
          itemCount: grid.children.length,
          savedAt: Date.now(),
        })
      );
    } catch (e) {
      /* sessionStorage disabled/full — fail silently, no scroll memory this time */
    }
  }

  function lcReadScrollMemory() {
    try {
      var raw = sessionStorage.getItem(lcScrollMemoryKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function lcClearScrollMemory() {
    try {
      sessionStorage.removeItem(lcScrollMemoryKey());
    } catch (e) {}
  }

  function lcBindProductClickSave(grid) {
    if (!grid || grid.dataset.lcScrollMemoryBound === 'true') return;
    grid.dataset.lcScrollMemoryBound = 'true';
    grid.addEventListener('click', function (e) {
      var link = e.target.closest('a[href*="/products/"]');
      if (link) lcSaveScrollMemory(grid);
    });
  }

  // True only when the browser's back/forward buttons (or an
  // equivalent in-site "go back" action) triggered this load — never
  // for a plain refresh or a fresh link click. That's what keeps a
  // manual F5 from unexpectedly restoring an old scroll position.
  function lcIsBackForwardNavigation() {
    try {
      var entries = performance.getEntriesByType('navigation');
      if (entries && entries.length) return entries[0].type === 'back_forward';
      if (performance.navigation) return performance.navigation.type === 2; // legacy Safari
    } catch (e) {}
    return false;
  }

  // Backup save — covers cases that skip the click handler (e.g. a
  // product opened via keyboard Enter that still bubbles a click will
  // already be caught above; this is for anything else that navigates
  // away, so "back" still has something recent to restore from).
  window.addEventListener('pagehide', function () {
    lcSaveScrollMemory();
  });

  // Restores scroll on a back/forward-cache restore. In this case the
  // DOM (including any extra infinite-scroll products) is exactly as
  // the user left it, so no rehydration is needed — just reposition.
  // event.persisted is only ever true for a real bfcache restore, so
  // this never fires on a plain refresh.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return; // fresh loads are handled in initInfiniteScroll()
    var saved = lcReadScrollMemory();
    if (!saved) return;
    window.scrollTo(0, saved.scrollY);
  });
  // ------------------------- END SCROLL MEMORY -------------------------

  function findNextLink(root) {
    const links = root.querySelectorAll('a[href*="page="]');
    return Array.from(links).find(function (a) {
      const txt = (a.textContent || '').trim();
      const aria = a.getAttribute('aria-label') || '';
      return /^next$/i.test(txt) || /next/i.test(aria);
    });
  }

  function findGrid(root) {
    return (
      root.querySelector('[data-fragment-id="results"] ul.collection-list') ||
      root.querySelector('[data-fragment-id="results"] ul') ||
      root.querySelector('ul.collection-list') ||
      root.querySelector('ul.grid')
    );
  }

  function isPlaceholder(el) {
    if (!el || el.nodeType !== 1) return true;
    if (el.getAttribute('aria-hidden') === 'true') return true;
    if (el.getAttribute('role') === 'presentation') return true;
    const cls = el.className || '';
    if (/placeholder/i.test(cls)) return true;
    if (el.children.length === 0 && !el.textContent.trim()) return true;
    return false;
  }

  function removePlaceholders(grid) {
    Array.from(grid.children).forEach(function (child) {
      if (isPlaceholder(child)) child.remove();
    });
  }

  // Shared per-product hydration (images, Alpine init, etc.) — used by
  // both the normal sequential loadMore() and the parallel catch-up path.
  function hydrateNewProduct(product) {
    product.querySelectorAll('img').forEach(function (img) {
      img.loading = 'eager';
      if (img.dataset.src && (!img.getAttribute('src') || img.src.startsWith('data:'))) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset && !img.getAttribute('srcset')) {
        img.srcset = img.dataset.srcset;
      }
    });
    product.querySelectorAll('image-with-placeholder').forEach(function (el) {
      el.classList.add('is-loaded', 'loaded', 'is-active');
    });
    product.querySelectorAll('[x-cloak]').forEach(function (el) {
      el.removeAttribute('x-cloak');
    });
    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      try { window.Alpine.initTree(product); } catch (e) {}
    }
  }

  function appendProductsFromDoc(productGrid, doc) {
    const newGrid = findGrid(doc);
    if (!newGrid) return;

    removePlaceholders(productGrid);

    const realProductsHtml = Array.from(newGrid.children)
      .filter(function (child) { return !isPlaceholder(child); })
      .map(function (child)   { return child.outerHTML; })
      .join('');

    const startIndex = productGrid.children.length;
    productGrid.insertAdjacentHTML('beforeend', realProductsHtml);

    Array.from(productGrid.children).slice(startIndex).forEach(hydrateNewProduct);
  }

  // Builds page N's URL by swapping the "page" query param on a known
  // pagination link (e.g. the scraped "next" href), preserving every
  // other param (filters, sort, etc.).
  function buildPageUrl(sampleUrl, pageNum) {
    try {
      const url = new URL(sampleUrl, window.location.origin);
      url.searchParams.set('page', pageNum);
      return url.toString();
    } catch (e) {
      return sampleUrl;
    }
  }

  // ---- State tracked across re-inits so we can clean up properly ----
  var activeObserver = null;
  var activeSentinel = null;
  var activeLoadingEl = null;
  var isInitialLoad = true;

  function cleanup() {
    if (activeObserver)  { activeObserver.disconnect(); activeObserver = null; }
    if (activeSentinel  && activeSentinel.parentNode)  { activeSentinel.parentNode.removeChild(activeSentinel);  activeSentinel  = null; }
    if (activeLoadingEl && activeLoadingEl.parentNode) { activeLoadingEl.parentNode.removeChild(activeLoadingEl); activeLoadingEl = null; }
  }

  function initInfiniteScroll() {
    cleanup();

    const productGrid = findGrid(document);
    if (!productGrid) return;

    lcBindProductClickSave(productGrid); // SCROLL MEMORY

    const initialNext = findNextLink(document);
    if (!initialNext || !initialNext.href) {
      // Only one page of results — remove the class so normal pagination shows
      document.documentElement.classList.remove('has-infinite-scroll');
      return;
    }

    document.documentElement.classList.add('has-infinite-scroll');

    let nextUrl = initialNext.href;
    let loading  = false;
    let done     = false;

    // SCROLL MEMORY: only treat this as "coming back" if the browser says
    // this load actually came from a back/forward action — never on a
    // plain refresh — and only ever on the very first init per page load.
    const scrollMemory =
      isInitialLoad && lcIsBackForwardNavigation() ? lcReadScrollMemory() : null;
    const pageSizeAtLoad = productGrid.children.length; // items already server-rendered

    // Scroll to top only on first page load, not when filters change —
    // unless we're about to restore a saved position instead.
    if (isInitialLoad) {
      if (!scrollMemory) {
        window.scrollTo(0, 0);
      }
      isInitialLoad = false;
    }

    const loadingEl = document.createElement('div');
    loadingEl.className = 'infinite-scroll-loading';
    loadingEl.innerHTML =
      '<div class="infinite-scroll-spinner" aria-hidden="true"></div>' +
      '<span>Loading more...</span>';
    loadingEl.style.display = 'none';
    loadingEl.setAttribute('aria-live', 'polite');
    activeLoadingEl = loadingEl;

    const sentinel = document.createElement('div');
    sentinel.className = 'infinite-scroll-sentinel';
    sentinel.style.cssText = 'height: 1px; width: 100%; margin: 0; pointer-events: none;';
    sentinel.setAttribute('aria-hidden', 'true');
    activeSentinel = sentinel;

    if (productGrid.parentNode) {
      productGrid.parentNode.insertBefore(loadingEl, productGrid.nextSibling);
      productGrid.parentNode.insertBefore(sentinel,  loadingEl.nextSibling);
    }

    async function loadMore() {
      if (loading || done) return;
      loading = true;
      loadingEl.style.display = 'flex';

      try {
        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error('Fetch failed: ' + response.status);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        appendProductsFromDoc(productGrid, doc);

        const newNext = findNextLink(doc);
        if (newNext && newNext.href && newNext.href !== nextUrl) {
          nextUrl = newNext.href;
        } else {
          done = true;
          observer.disconnect();
          sentinel.remove();
          loadingEl.remove();
        }
      } catch (err) {
        console.error('Infinite scroll error:', err);
        done = true;
        observer.disconnect();
        loadingEl.remove();
      } finally {
        loading = false;
        if (!done) loadingEl.style.display = 'none';
      }
    }

    const observer = new IntersectionObserver(
      function (entries) { if (entries[0].isIntersecting) loadMore(); },
      { rootMargin: '600px 0px' }
    );
    activeObserver = observer;
    observer.observe(sentinel);

    // SCROLL MEMORY: fresh (non-bfcache) back/forward load with a saved
    // position for this URL. Fetch every needed page IN PARALLEL (much
    // faster than one-by-one), append them in order, then scroll.
    if (scrollMemory) {
      (async function rehydrateAndRestore(target) {
        loadingEl.style.display = 'flex';
        var labelEl = loadingEl.querySelector('span');
        if (labelEl) labelEl.textContent = 'Restoring your place...';

        if (pageSizeAtLoad > 0 && target.itemCount > pageSizeAtLoad) {
          var pagesNeeded = Math.ceil(target.itemCount / pageSizeAtLoad) - 1;
          var pageUrls = [];
          for (var p = 2; p <= pagesNeeded + 1; p++) {
            pageUrls.push(buildPageUrl(nextUrl, p));
          }

          try {
            var htmlResults = await Promise.all(
              pageUrls.map(function (url) {
                return fetch(url).then(function (r) { return r.ok ? r.text() : null; });
              })
            );

            htmlResults.forEach(function (html) {
              if (!html) return;
              appendProductsFromDoc(productGrid, new DOMParser().parseFromString(html, 'text/html'));
            });

            const lastHtml = htmlResults[htmlResults.length - 1];
            if (lastHtml) {
              const lastDoc = new DOMParser().parseFromString(lastHtml, 'text/html');
              const newNext = findNextLink(lastDoc);
              if (newNext && newNext.href) {
                nextUrl = newNext.href;
              } else {
                done = true;
                observer.disconnect();
                sentinel.remove();
              }
            }
          } catch (err) {
            console.error('Scroll-memory rehydrate error:', err);
            // Falls through to the top-up loop below, which will retry
            // sequentially via the normal loadMore() path.
          }
        }

        // Top up sequentially in case the parallel batch under/over-shot
        // (e.g. inconsistent page size) or the batch above failed.
        while (!done && productGrid.children.length < target.itemCount) {
          await loadMore();
        }

        requestAnimationFrame(function () {
          window.scrollTo(0, target.scrollY);
          // Late image loads can shift layout slightly — one correction pass.
          setTimeout(function () {
            window.scrollTo(0, target.scrollY);
          }, 350);
        });
        if (loadingEl && loadingEl.parentNode && !done) loadingEl.style.display = 'none';
        lcClearScrollMemory();
      })(scrollMemory);
    }
  }

  // ---- Re-init after filter-driven navigation ----

  // Waits for the results container to actually update its DOM, then re-inits.
  // Using a MutationObserver is more reliable than a fixed timeout because
  // it fires exactly when the new content lands, regardless of network speed.
  function waitForResultsAndReinit() {
    const resultsEl = document.querySelector('[data-fragment-id="results"]');

    if (!resultsEl) {
      // Fallback: no results fragment found, just wait a beat
      setTimeout(initInfiniteScroll, 500);
      return;
    }

    const mo = new MutationObserver(function (mutations) {
      const hasChanges = mutations.some(function (m) {
        return m.addedNodes.length > 0 || m.removedNodes.length > 0;
      });
      if (!hasChanges) return;
      mo.disconnect();
      // Small extra delay lets Alpine/Splide finish their own init
      setTimeout(initInfiniteScroll, 150);
    });

    mo.observe(resultsEl, { childList: true, subtree: false });

    // Safety valve — disconnect after 8s if nothing ever changed
    setTimeout(function () { mo.disconnect(); }, 8000);
  }

  function setupFilterReinit() {
    // Intercept pushState — this is what Shopify filter JS calls when the user
    // picks a filter and the URL updates before the AJAX fetch runs.
    const origPushState = history.pushState.bind(history);
    history.pushState = function () {
      origPushState.apply(history, arguments);
      waitForResultsAndReinit();
    };

    // Back / forward buttons also change the results
    window.addEventListener('popstate', waitForResultsAndReinit);
  }

  function init() {
    initInfiniteScroll();
    // Only wire up the filter watcher on collection pages
    if (window.location.pathname.includes('/collections/')) {
      setupFilterReinit();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// =====================================================================
// Klaviyo Back-In-Stock — per-variant "Notify Me" button
//
// When a customer selects an OOS variant, this hides the disabled
// "Sold Out" button and injects an active "Notify Me" button in its
// place. Clicking it opens your Klaviyo BIS popup form.
//
// SETUP: Replace 'KLAVIYO_FORM_ID' with your actual form ID.
// Find it in Klaviyo → Sign-up forms → [your BIS form] → Embed.
//
// STOQ CLEANUP: this used to bail out early whenever a Stoq/Restock
// Rocket element was on the page (`.restock-rocket-form-input`,
// `[class*=stoq]`, `[data-stoq]`), so it wouldn't fight with Stoq's own
// CTA for genuinely out-of-stock variants. Stoq's embed is off on this
// theme now, so that guard was removed — this always runs its own
// check instead.
// =====================================================================
(function () {
  const KLAVIYO_FORM_ID = 'TqSh6C';
  var lcOosBound = false; // replaces document.dataset.lcOosBound (document.dataset is undefined in some browsers)

  if (!window.location.pathname.includes('/products/')) return;

  // ---- Klaviyo re-injection watcher ----
  // Klaviyo sometimes injects #klaviyo-bis-button-container *after* showNotifyBtn()
  // already hid it. This observer catches late injections and hides the container
  // immediately, preventing the double-button issue.
  var klReinjectObserver = null;

  function startKlWatcher() {
    if (klReinjectObserver) return;
    klReinjectObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var nodes = mutations[i].addedNodes;
        for (var j = 0; j < nodes.length; j++) {
          var node = nodes[j];
          if (node.nodeType !== 1) continue;
          var klEl =
            node.id === 'klaviyo-bis-button-container'
              ? node
              : node.querySelector && node.querySelector('#klaviyo-bis-button-container');
          if (klEl) klEl.style.display = 'none';
        }
      }
    });
    klReinjectObserver.observe(document.body, { childList: true, subtree: true });
  }

  function stopKlWatcher() {
    if (klReinjectObserver) {
      klReinjectObserver.disconnect();
      klReinjectObserver = null;
    }
  }

  function getATCBtn() {
    return document.querySelector('form[id^="product-form-"] .add-to-cart-container button[type="submit"]');
  }

  function getNotifyBtn() {
    return document.querySelector('.lc-notify-me-btn');
  }

  // Returns true when the selected variant is OOS. Reads
  // currentVariantAvailable directly from Alpine so we don't
  // accidentally treat the transient `loading: true` state (during
  // cart add) as OOS.
  function isCurrentVariantOOS() {
    // Primary: read from Alpine's ProductForm data
    const formIsland = document.querySelector('[x-data="ProductForm"]');
    if (formIsland && window.Alpine) {
      try {
        const data = window.Alpine.$data(formIsland);
        if (data && typeof data.currentVariantAvailable !== 'undefined') {
          if (data.loading) return false; // cart add in progress — not OOS
          return !data.currentVariantAvailable;
        }
      } catch (e) { /* fall through to button check */ }
    }

    // Fallback: check button disabled attribute (excluding loading state)
    const btn = getATCBtn();
    if (!btn) return false;
    if (btn.getAttribute('aria-busy') === 'true') return false; // loading indicator
    return btn.disabled || btn.getAttribute('aria-disabled') === 'true';
  }

  function openKlaviyoForm() {
    // Unhide Klaviyo's container (we may have hidden it in showNotifyBtn),
    // then click its button — this is the most reliable way to open the BIS popup.
    var klContainer = document.querySelector('#klaviyo-bis-button-container');
    var klBtn = klContainer ? klContainer.querySelector('button') : null;
    if (klBtn) {
      klContainer.style.removeProperty('display');
      klBtn.click();
      klContainer.style.display = 'none'; // re-hide immediately after triggering the click
      return;
    }
    // Fallback if Klaviyo hasn't injected its button yet
    setTimeout(function () {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', KLAVIYO_FORM_ID]);
    }, 0);
  }

  // Shows our Notify Me button and hides the disabled ATC button.
  // Also hides any Klaviyo-injected BIS element so there's no duplicate.
  function showNotifyBtn() {
    const atcBtn = getATCBtn();
    if (!atcBtn || getNotifyBtn()) return;

    const notifyBtn = document.createElement('button');
    notifyBtn.type = 'button';
    notifyBtn.className = 'lc-notify-me-btn';
    notifyBtn.textContent = 'Notify Me';
    notifyBtn.addEventListener('click', openKlaviyoForm);

    atcBtn.style.display = 'none';
    atcBtn.parentNode.insertBefore(notifyBtn, atcBtn);

    // Hide Klaviyo's injected button via JS so it doesn't show alongside ours.
    // We DON'T use CSS display:none because that breaks programmatic .click().
    var klContainer = document.querySelector('#klaviyo-bis-button-container');
    if (klContainer) klContainer.style.display = 'none';

    // Watch for Klaviyo re-injecting its button after this point
    startKlWatcher();
  }

  // Restores the ATC button and removes our Notify Me button.
  function hideNotifyBtn() {
    const notifyBtn = getNotifyBtn();
    if (notifyBtn) notifyBtn.remove();

    const atcBtn = getATCBtn();
    if (atcBtn) atcBtn.style.display = '';

    // Restore Klaviyo's container so it can be clicked normally
    var klContainer = document.querySelector('#klaviyo-bis-button-container');
    if (klContainer) klContainer.style.removeProperty('display');

    // Stop watching for Klaviyo re-injections
    stopKlWatcher();
  }

  function syncNotifyButton() {
    if (isCurrentVariantOOS()) {
      showNotifyBtn();
    } else {
      hideNotifyBtn();
    }
  }

  // Uses event delegation on the document so it works regardless of when
  // the OOS class is applied (resizable-option.js adds .out-of-stock after
  // 1 s, which is after our 500 ms init — direct binding would miss them).
  function setupOOSChipListeners() {
    if (lcOosBound) return;
    lcOosBound = true;

    document.addEventListener('click', function (e) {
      var el = e.target;
      var oosChip =
        el.closest('.label-only-input[data-unavailable="true"]') ||
        el.closest('.resizable-option.out-of-stock') ||
        el.closest('.bundle-label[data-unavailable="true"]') ||
        el.closest('.bundle-resizable-span[data-unavailable="true"]');

      if (!oosChip) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      openKlaviyoForm();
    }, true); // capture phase — runs before picker JS handlers
  }

  function init() {
    // OOS chip listeners run immediately — they don't need the ATC container
    setupOOSChipListeners();

    // The inline Notify Me button requires the ATC container — wait for Alpine
    setTimeout(function () {
      const atcContainer = document.querySelector('form[id^="product-form-"] .add-to-cart-container');
      if (!atcContainer) return;

      // Watch Alpine toggling the button's disabled attribute on variant change
      const observer = new MutationObserver(syncNotifyButton);
      observer.observe(atcContainer, {
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled']
      });

      // Also catch any custom events the theme fires on variant change
      document.addEventListener('theme:variant:change', syncNotifyButton);

      syncNotifyButton(); // check initial state
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// =====================================================================
// Scroll to top button
//
// Threshold (in viewport heights) comes from the button's
// data-scroll-threshold attribute, which snippets/scroll-to-top.liquid
// sets from Theme settings (enable_scroll_to_top /
// scroll_to_top_threshold_standard / scroll_to_top_threshold_pdp).
//
// Dispatches two events other scripts/analytics can listen for:
//   - "lc:scroll-to-top-shown"   fired once per page load, the first
//                                 time the button becomes visible
//   - "lc:scroll-to-top-clicked" fired every time it's clicked
// =====================================================================
(function () {
  function init() {
    var btn = document.getElementById('lc-scroll-to-top');
    if (!btn || btn.dataset.lcSttBound === 'true') return;
    btn.dataset.lcSttBound = 'true';

    var thresholdMultiplier = parseFloat(btn.dataset.scrollThreshold) || 2;
    var ticking = false;
    var hasFiredShownEvent = false;

    function thresholdPx() {
      // Recalculated live so resizing/rotating keeps it accurate.
      return window.innerHeight * thresholdMultiplier;
    }

    function update() {
      var shouldShow = window.scrollY > thresholdPx();

      if (shouldShow !== btn.classList.contains('is-visible')) {
        btn.classList.toggle('is-visible', shouldShow);
        btn.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        btn.tabIndex = shouldShow ? 0 : -1;

        if (shouldShow && !hasFiredShownEvent) {
          hasFiredShownEvent = true;
          document.dispatchEvent(new CustomEvent('lc:scroll-to-top-shown'));
        }
      }

      ticking = false;
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.dispatchEvent(new CustomEvent('lc:scroll-to-top-clicked'));
    });

    update();
    initChatWidgetAlignment(btn);
  }

  // ---------------------------------------------------------------------
  // Align vertically with the chat widget bubble (Gorgias — confirmed
  // via its iframe title on this store). Reads its actual on-screen
  // bottom offset so the two buttons sit at the same height, no matter
  // the breakpoint. Falls back to the CSS default (in scroll-to-top.css)
  // if no known chat widget is found.
  // ---------------------------------------------------------------------
  var CHAT_WIDGET_SELECTORS = [
    'iframe[title="Gorgias live chat messenger"]', // confirmed selector for this store
    '#gorgias-chat-container',
    '#gorgias-chat-widget',
    '.gorgias-chat-container',
    '#chat-widget-container', // Zendesk
    '#launcher', // Zendesk classic
    '.intercom-launcher',
    '#tidio-chat-iframe',
  ];

  // Manual fine-tune on top of the detected position. The Gorgias launcher
  // iframe has some invisible padding around the visible bubble, so the
  // raw bounding box isn't a perfect match — this nudges the button up
  // to compensate. Adjust this number if it drifts after a Gorgias update.
  var CHAT_WIDGET_ALIGNMENT_CORRECTION_PX = 10;

  function initChatWidgetAlignment(btn) {
    var attempts = 0;

    function findChatWidget() {
      for (var i = 0; i < CHAT_WIDGET_SELECTORS.length; i++) {
        var el = document.querySelector(CHAT_WIDGET_SELECTORS[i]);
        if (el && el.getBoundingClientRect().height > 0) return el;
      }
      return null;
    }

    function align() {
      var chatEl = findChatWidget();
      if (!chatEl) {
        attempts += 1;
        if (attempts < 6) setTimeout(align, 800); // widget likely still loading async
        return;
      }
      var rect = chatEl.getBoundingClientRect();
      var bottomOffset = (window.innerHeight - rect.bottom) + CHAT_WIDGET_ALIGNMENT_CORRECTION_PX;
      if (bottomOffset >= 0 && bottomOffset < 200) {
        btn.style.bottom = 'calc(' + Math.round(bottomOffset) + 'px + env(safe-area-inset-bottom, 0px))';
      }
    }

    align();
    window.addEventListener('resize', align);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-bind if the section/layout re-renders in the theme editor
  document.addEventListener('shopify:section:load', init);
})();


// find your size
//
// Clicking any "find your size" link (footer, About menu, size guide
// drawer, etc.):
//   - If the Aitaca widget is already on the current page (ring PDP),
//     opens it directly, no navigation.
//   - Otherwise redirects to a ring product page that has the widget
//     (Till Death Ring | Silver, handle "till-death" — permanent
//     best-seller) and opens the widget there automatically once it
//     renders. This is done via the "open-size-finder=1" query param,
//     which checkAutoOpenParam() picks up on the next page load.
(function () {
  var REDIRECT_PRODUCT_URL = '/products/till-death';

  function mergeSizeRow() {
    var aitaca = document.querySelector('.aitaca-widget-container');
    if (!aitaca || aitaca.dataset.merged) return;

    var prompt = document.createElement('span');
    prompt.className = 'lc-size-prompt';
    prompt.textContent = "Not sure?";

    var wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin:12px 0 0;';
    aitaca.style.margin = '0';

    aitaca.parentNode.insertBefore(wrapper, aitaca);
    wrapper.appendChild(prompt);
    wrapper.appendChild(aitaca);
    aitaca.dataset.merged = 'true';
  }

  document.addEventListener('DOMContentLoaded', mergeSizeRow);
  var mergeObserver = new MutationObserver(mergeSizeRow);
  mergeObserver.observe(document.body, { childList: true, subtree: true });
  setTimeout(function () { mergeObserver.disconnect(); }, 5000);

  function closeDrawerIfOpen() {
    try {
      if (window.Alpine && typeof Alpine.store === 'function') {
        var modalsStore = Alpine.store('modals');
        if (modalsStore && typeof modalsStore.close === 'function') {
          modalsStore.close('rightDrawer');
        }
      }
    } catch (err) {
      console.warn('No se pudo cerrar el drawer:', err);
    }
  }

  // Polls for .aitaca-button (it can take a moment to render after a
  // fresh navigation) and clicks it as soon as it shows up.
  function waitAndOpenAitaca(maxWaitMs) {
    var start = Date.now();
    var interval = setInterval(function () {
      var btn = document.querySelector('.aitaca-button');
      if (btn) {
        clearInterval(interval);
        btn.click();
      } else if (Date.now() - start > maxWaitMs) {
        clearInterval(interval);
        console.warn('No se encontró .aitaca-button luego de redirigir a ' + REDIRECT_PRODUCT_URL);
      }
    }, 150);
  }

  // If we just landed here via a "find your size" redirect, open the
  // widget automatically once it renders, then strip the param from
  // the URL so a refresh/back doesn't re-trigger it.
  function checkAutoOpenParam() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('open-size-finder') !== '1') return;

    waitAndOpenAitaca(8000);

    params.delete('open-size-finder');
    var newSearch = params.toString();
    var newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + window.location.hash;
    history.replaceState(null, '', newUrl);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', checkAutoOpenParam);
  } else {
    checkAutoOpenParam();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('a[href*="findyourringsize"]');
    if (!trigger) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var aitacaButton = document.querySelector('.aitaca-button');

    if (aitacaButton) {
      closeDrawerIfOpen();
      setTimeout(function () {
        aitacaButton.click();
      }, 50);
      return;
    }

    closeDrawerIfOpen();
    window.location.href = REDIRECT_PRODUCT_URL + '?open-size-finder=1';
  }, true);
})();